import {
  CreateDomainConfigInput as BaseCreateDomainConfigInput,
  CreateDomainConfigResult as BaseCreateDomainConfigResult,
  DomainConfig as BaseDomainConfig,
  DomainConfigChange as BaseDomainConfigChange,
  ListDomainConfigChangesResult as BaseListDomainConfigChangesResult,
  ResolveDomainConfigResult as BaseResolveDomainConfigResult,
  UpdateDomainConfigInput as BaseUpdateDomainConfigInput,
  UpdateDomainConfigResult as BaseUpdateDomainConfigResult
} from '@blisspointmedia/bpm-types/dist/DigitalAudit';
import { ClientType, ClientTypeType } from './digital-audit-constants';
import { CLIENT_TYPES, VERTICALS } from './engine/journey-data';
import { EditableMilestone } from './engine/journey-mapper-constants';
import { ReportRunSummary } from './report-runs-constants';

/**
 * The persisted, canonical row for a domain this tool has ever scanned - one
 * per domain, holding the client-context fields (client_type/vertical/
 * client_name) and any milestone edits, so a repeat scan of the same domain
 * doesn't force the user to re-enter them. Backed by the digitalaudit
 * service's `/digital-audit/domain-config*` endpoints (see domain-config-api.ts).
 *
 * Snake_case, matching the wire response 1:1 (see bpm-lambdas'
 * domainConfigStore.ts / bpm-types' DigitalAudit.ts) - no camelCase
 * remapping anywhere in this feature's request/response bodies.
 *
 * bpm-types keeps `client_type`/`edited_milestones` loosely typed (`string`/
 * `unknown`) since bpm-lambdas has no dependency on this app's ClientType/
 * EditableMilestone types - narrowed here at this app's own API boundary.
 */
export interface DomainConfig extends Omit<BaseDomainConfig, 'client_type' | 'edited_milestones'> {
  client_type: ClientTypeType;
  edited_milestones: EditableMilestone[] | null;
}

export interface ResolveDomainConfigResult extends Omit<
  BaseResolveDomainConfigResult,
  'config' | 'last_run'
> {
  config: DomainConfig;
  last_run: ReportRunSummary | null;
}

export interface CreateDomainConfigInput extends Omit<BaseCreateDomainConfigInput, 'client_type'> {
  client_type: ClientTypeType;
}

export interface CreateDomainConfigResult extends Omit<BaseCreateDomainConfigResult, 'config'> {
  config: DomainConfig;
}

export interface UpdateDomainConfigInput extends Omit<
  BaseUpdateDomainConfigInput,
  'client_type' | 'edited_milestones'
> {
  client_type?: ClientTypeType;
  // null resets to the template; omit to leave the saved edits unchanged.
  edited_milestones?: EditableMilestone[] | null;
}

export interface UpdateDomainConfigResult extends Omit<BaseUpdateDomainConfigResult, 'config'> {
  config: DomainConfig;
}

export type DomainConfigChange = BaseDomainConfigChange;
export type ListDomainConfigChangesResult = BaseListDomainConfigChangesResult;

export const CLIENT_TYPE_OPTIONS = Object.values(ClientType).map(key => ({
  key,
  label: CLIENT_TYPES[key]
}));

export const VERTICAL_OPTIONS = Object.values(VERTICALS)
  .map(v => ({ key: v.key, label: v.label }))
  .sort((a, b) => a.label.localeCompare(b.label));

export const DOMAIN_CONFIG_QUERY_KEY = 'digital-audit-domain-config';
